import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UsuariosService, DepartamentosService, UnidadesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Plus, 
  Download, 
  Users as UsersIcon
} from 'lucide-react';
import UserNotifications from '../../components/UserNotifications';
import UserStats from '../../components/Users/UserStats';
import UserFilters from '../../components/Users/UserFilters';
import UserTable from '../../components/Users/UserTable';
import UserPagination from '../../components/Users/UserPagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import toast from 'react-hot-toast';
import { Usuario, Departamento, Unidade } from '../../types';

interface Filters {
  status: string;
  nivel: string;
}

interface Notification {
  id: number;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
  details?: string;
}

interface Stats {
  total: number;
  ativos: number;
  inativos: number;
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

const Users: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<Usuario[]>([]);
  const [departments, setDepartments] = useState<Departamento[]>([]);
  const [units, setUnits] = useState<Unidade[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [filters, setFilters] = useState<Filters>({
    status: 'all',
    nivel: 'all'
  });
  const [sortBy, setSortBy] = useState<string>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  
  // Modal de confirmação de exclusão
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: 0,
    userName: ''
  });

  // Notificações
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Verificar se o usuário tem acesso
  useEffect(() => {
    if (usuario && usuario.nivel !== 'dev') {
      navigate('/dashboard');
      toast.error('Acesso negado. Apenas desenvolvedores podem acessar esta página.');
      return;
    }

    if (usuario) {
      loadUsers();
      loadDepartments();
      loadUnits();
    }
  }, [usuario, navigate]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await UsuariosService.getUsuarios();
      setUsers(response || []);
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para acessar esta página');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await DepartamentosService.getDepartamentos();
      setDepartments(response || []);
    } catch (error: any) {
      console.error('Erro ao carregar departamentos:', error);
    }
  };

  const loadUnits = async () => {
    try {
      const response = await UnidadesService.getUnidades();
      setUnits(response || []);
    } catch (error: any) {
      console.error('Erro ao carregar unidades:', error);
    }
  };

  // Adicionar notificação
  const addNotification = (type: 'success' | 'error' | 'warning' | 'info', message: string, title?: string, details?: string) => {
    const id = Date.now();
    const notification: Notification = {
      id,
      type,
      message,
      title,
      details
    };
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remover após 5 segundos
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Remover notificação
  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Filtros e busca
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.nome.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || 
                         (filters.status === 'active' && user.status === 1) ||
                         (filters.status === 'inactive' && user.status === 0);
    
    const matchesNivel = filters.nivel === 'all' || user.nivel === filters.nivel;
    
    return matchesSearch && matchesStatus && matchesNivel;
  });

  // Ordenação
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue: any = a[sortBy as keyof Usuario];
    let bValue: any = b[sortBy as keyof Usuario];
    
    if (sortBy === 'nome') {
      aValue = a.nome.toLowerCase();
      bValue = b.nome.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Calcular paginação
  const totalItems = sortedUsers.length;
  const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);

  // Atualizar paginação quando os filtros mudarem
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems,
      totalPages
    }));
  }, [totalItems, totalPages]);

  // Obter itens da página atual
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return sortedUsers.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  // Seleção de usuários
  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === getCurrentPageItems().length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(getCurrentPageItems().map(user => user.id));
    }
  };

  // Bulk actions
  const handleBulkDelete = () => {
    if (confirm(`Tem certeza que deseja excluir ${selectedUsers.length} usuário(s)?`)) {
      setUsers(prev => prev.filter(user => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);
      addNotification('success', `${selectedUsers.length} usuário(s) excluído(s) com sucesso!`, 'Usuários Excluídos');
    }
  };

  const handleBulkStatus = (newStatus: number) => {
    const action = newStatus === 1 ? 'ativado(s)' : 'desativado(s)';
    setUsers(prev => prev.map(user => 
      selectedUsers.includes(user.id) ? { ...user, status: newStatus } : user
    ));
    setSelectedUsers([]);
    addNotification('success', `${selectedUsers.length} usuário(s) ${action} com sucesso!`, 'Status Alterado');
  };

  // Exclusão individual
  const handleUserDelete = (userId: number, userName: string) => {
    setDeleteModal({
      isOpen: true,
      userId,
      userName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      userId: 0,
      userName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      await UsuariosService.deleteUsuario(deleteModal.userId);
      setUsers(prev => prev.filter(u => u.id !== deleteModal.userId));
      addNotification('success', `Usuário "${deleteModal.userName}" excluído com sucesso!`, 'Usuário Excluído');
      closeDeleteModal();
    } catch (error: any) {
      console.error('Erro ao excluir usuário:', error);
      if (error.message?.includes('Acesso negado')) {
        addNotification('error', 'Você não tem permissão para excluir usuários.', 'Acesso Negado');
      } else {
        addNotification('error', 'Erro ao excluir usuário. Tente novamente.', 'Erro na Exclusão');
      }
    }
  };

  // Exportar dados
  const exportToCSV = () => {
    try {
      const headers = ['Nome', 'Nível', 'Status', 'Última Atividade'];
      const csvContent = [
        headers.join(','),
        ...sortedUsers.map(user => [
          user.nome,
          user.nivel,
          user.status === 1 ? 'Ativo' : 'Inativo',
          user.ultima_atividade ? new Date(user.ultima_atividade).toLocaleDateString('pt-BR') : '-'
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      addNotification('success', `Exportação concluída! ${sortedUsers.length} usuário(s) exportado(s).`, 'Exportação CSV');
    } catch (error) {
      addNotification('error', 'Erro ao exportar dados. Tente novamente.', 'Erro na Exportação', error instanceof Error ? error.message : 'Erro desconhecido');
    }
  };

  // Estatísticas
  const stats: Stats = {
    total: users.length,
    ativos: users.filter(u => u.status === 1).length,
    inativos: users.filter(u => u.status === 0).length
  };

  // Se não for dev, não renderizar nada (será redirecionado)
  if (!usuario || usuario.nivel !== 'dev') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
              <UsersIcon className="text-primary-600 dark:text-primary-400" size={28} />
              Gerenciamento de Usuários
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Gerencie usuários, permissões e acessos do sistema
            </p>
            <div className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Total de usuários: {users.length} • Filtrados: {filteredUsers.length}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <Link to="/usuarios/adicionar" className="btn btn-primary w-full sm:w-auto justify-center flex items-center gap-2">
              <Plus size={20} />
              Novo Usuário
            </Link>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <UserStats stats={stats} />

      {/* Filtros e Busca */}
      <UserFilters
        filters={filters}
        searchTerm={searchTerm}
        onFiltersChange={setFilters}
        onSearchChange={setSearchTerm}
        selectedUsers={selectedUsers}
        onBulkStatus={handleBulkStatus}
        onBulkDelete={handleBulkDelete}
      />

      {/* Tabela de usuários */}
      <UserTable
        users={getCurrentPageItems()}
        selectedUsers={selectedUsers}
        onUserSelection={toggleUserSelection}
        onSelectAll={toggleAllUsers}
        onUserDelete={handleUserDelete}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={(field) => {
          if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
          } else {
            setSortBy(field);
            setSortOrder('asc');
          }
        }}
      />

      {/* Estado vazio */}
      {getCurrentPageItems().length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Nenhum usuário encontrado</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Tente ajustar os filtros de busca ou criar um novo usuário.</p>
          <Link to="/usuarios/adicionar" className="btn btn-primary">
            <Plus size={20} />
            Criar Primeiro Usuário
          </Link>
        </div>
      )}

      {/* Paginação */}
      <UserPagination
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      {/* Barra de ações */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
          Mostrando {getCurrentPageItems().length} de {users.length} usuário(s)
        </div>
        
        <div className="flex items-center justify-center sm:justify-end">
          <button
            onClick={exportToCSV}
            className="btn btn-outline btn-sm flex items-center gap-2"
          >
            <Download size={16} />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Usuário"
        message={`Tem certeza que deseja excluir o usuário "${deleteModal.userName}"? Esta ação não pode ser desfeita. Todos os dados do usuário serão removidos permanentemente.`}
        confirmText="Excluir Usuário"
        type="danger"
      />

      {/* Notificações */}
      <UserNotifications
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default Users;
